import * as React from "react";
import type { RecentItem } from "../../types/recent";
import { timeago } from "../../utils/time";
import { Button, Tag } from "antd";

type RecentsPanelProps = {
  items: RecentItem[];
  onRemove: (id: string) => void;
  onClear: () => void;
  onItemClick?: (item: RecentItem) => void;
  loading?: boolean;
  error?: string | null;
  title?: string;
};

/**
 * Accessible, lightweight Recents list UI with keyboard navigation.
 */
export function RecentsPanel(props: RecentsPanelProps) {
  const {
    items,
    onRemove,
    onClear,
    onItemClick,
    loading = false,
    error = null,
  } = props;

  const [activeIndex, setActiveIndex] = React.useState<number>(
    items.length > 0 ? 0 : -1,
  );
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  React.useEffect(() => {
    setActiveIndex(items.length > 0 ? 0 : -1);
  }, [items.length]);

  React.useEffect(() => {
    if (activeIndex >= 0) {
      itemRefs.current[activeIndex]?.focus();
    }
  }, [activeIndex]);

  const onKeyDownContainer = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (items.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((idx) => Math.min(idx + 1, items.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((idx) => Math.max(idx - 1, 0));
      } else if (e.key === "Enter" && activeIndex >= 0) {
        e.preventDefault();
        const current = items[activeIndex];
        if (onItemClick && current) onItemClick(current);
      }
    },
    [activeIndex, items, onItemClick],
  );

  const now = Date.now();

  return (
    <section
      className="recents-panel"
      onKeyDown={onKeyDownContainer}
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <header
        className="recents-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 8,
          padding: "16px 16px 0 16px",
          flex: "0 0 auto",
        }}
      >
        {items.length > 0 && (
          <Button
            type="text"
            onClick={onClear}
            disabled={items.length === 0}
            aria-disabled={items.length === 0}
            title="Clear all recently viewed items"
          >
            Clear all
          </Button>
        )}
      </header>

      {loading && (
        <div
          role="status"
          aria-live="polite"
          style={{ marginTop: 8, color: "#666", fontSize: 12 }}
        >
          Loading…
        </div>
      )}
      {error && (
        <div
          role="alert"
          style={{ marginTop: 8, color: "#b00020", fontSize: 12 }}
        >
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="text-center py-12">
          <svg
            className="w-16 h-16 mx-auto text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-gray-500 text-sm">No recently viewed items</p>
          <p className="text-gray-400 text-xs mt-1">
            Your recent projects will appear here
          </p>
        </div>
      )}

      {items.length > 0 && (
        <ul
          aria-label="Recent items"
          style={{
            listStyle: "none",
            padding: "0 16px",
            marginTop: 12,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            scrollbarWidth: "none",
          }}
        >
          {items.map((item, idx) => {
            const isActive = idx === activeIndex;
            return (
              <li key={item.id} style={{ marginBottom: 6 }}>
                <div
                  className="transition-all duration-200 hover:shadow-md"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    border: "1px solid #e5e7eb",
                    padding: 12,
                    borderRadius: 8,
                    background: isActive ? "#f0fdfa" : "#fff",
                  }}
                >
                  {item.thumbnailUrl ? (
                    <img
                      src={item.thumbnailUrl}
                      alt={`Thumbnail for ${item.title}`}
                      width={28}
                      height={28}
                      style={{ borderRadius: 4, objectFit: "cover" }}
                    />
                  ) : (
                    <div
                      aria-hidden="true"
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 4,
                        background: "#e9ecef",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        color: "#6c757d",
                      }}
                      title={item.type ? `${item.type} item` : "Item"}
                    >
                      {item.type?.[0]?.toUpperCase() ?? "•"}
                    </div>
                  )}

                  <button
                    ref={(el) => (itemRefs.current[idx] = el)}
                    type="button"
                    onClick={() => onItemClick?.(item)}
                    title={item.title}
                    tabIndex={isActive ? 0 : -1}
                    aria-current={isActive ? "true" : undefined}
                    className="recent-row"
                    style={{
                      flex: 1,
                      textAlign: "left",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                        }}
                      >
                        {item.title}
                      </span>
                      {item.type && (
                        <Tag
                          style={{ borderRadius: 999, padding: "1px 12px" }}
                          color={
                            item.type === "Project"
                              ? "purple"
                              : item.type === "Project Page"
                                ? "green"
                                : item.type === "Project Page Component"
                                  ? "magenta"
                                  : item.type === "Qual Step"
                                    ? "gold"
                                    : item.type === "Project Objective"
                                      ? "blue"
                                      : "error"
                          }
                        >
                          {item.type}
                        </Tag>
                      )}
                    </div>
                    <div
                      style={{ fontSize: 11, color: "#6c757d", marginTop: 2 }}
                    >
                      Last viewed{" "}
                      {timeago(Math.max(0, now - item.lastAccessedAt))} ago
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => onRemove(item.id)}
                    aria-label={`Remove ${item.title}`}
                    title="Remove"
                    className="btn-remove"
                    style={{
                      padding: "4px 6px",
                      fontSize: 12,
                      border: "1px solid #eee",
                      borderRadius: 4,
                      background: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    ✕
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default RecentsPanel;
